/**
 * Database Configuration
 * Manages database configuration and initialization for different environments
 */

import { DATABASE_TYPES } from './interfaces/IDatabaseAdapter.js'
// MongoDB-only configuration

/**
 * Get database configuration based on environment
 * @returns {Object} Database configuration
 */
export function getDatabaseConfig() {
  // Get database type from environment variable or default to MongoDB
  const dbType = import.meta.env.VITE_DATABASE_TYPE || DATABASE_TYPES.MONGODB
  
  switch (dbType) {
    case DATABASE_TYPES.HTTP:
      // Use nullish coalescing to handle empty strings correctly
      // Empty string enables Vite proxy for local development
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'https://dev.services.leadconnectorhq.com/hltestmanager'
      return {
        type: DATABASE_TYPES.HTTP,
        config: {
          baseUrl: apiBaseUrl,
          timeout: 10000
        }
      }
    
    case DATABASE_TYPES.MONGODB:
      return {
        type: DATABASE_TYPES.MONGODB,
        config: {
          connectionString: import.meta.env.VITE_MONGODB_CONNECTION_STRING,
          database: import.meta.env.VITE_MONGODB_DATABASE || 'tester_mongo',
          options: {
            useNewUrlParser: true,
            useUnifiedTopology: true
          }
        }
      }
    
    default:
      throw new Error(`Unsupported database type: ${dbType}`)
  }
}

/**
 * Get collection names mapping
 * This allows for different collection/table names across databases
 */
export const COLLECTIONS = {
  USERS: 'users',
  TEAMS: 'teams',
  PROJECTS: 'projects',
  TEST_CASES: 'test_cases',
  TEST_RUNS: 'test_runs',
  BUGS: 'bugs',
  FOLDERS: 'folders', // For test case organization
  FEATURES: 'features', // For feature tracking
  NOTIFICATIONS: 'notifications',
  ACTIVITIES: 'activities',
  COMMENTS: 'comments',
  REQUIREMENTS: 'requirements',
  REQUIREMENT_MAPPINGS: 'requirement_mappings',
  TEST_RUN_CASES: 'test_run_cases'
}

/**
 * Database schema definitions for different entities
 * This helps with validation and ensuring consistency across databases
 */
export const SCHEMAS = {
  [COLLECTIONS.USERS]: {
    id: { type: 'string', required: true },
    name: { type: 'string', required: true },
    email: { type: 'string', required: true },
    role: { type: 'string', required: true, enum: ['admin', 'qa_manager', 'developer', 'tester'] },
    teams: { type: 'array', default: [] },
    permissions: { type: 'array', default: [] },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.TEAMS]: {
    id: { type: 'string', required: true },
    name: { type: 'string', required: true },
    description: { type: 'string' },
    members: { type: 'array', default: [] },
    memberRoles: { type: 'object', default: {} },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.PROJECTS]: {
    id: { type: 'string', required: true },
    name: { type: 'string', required: true },
    description: { type: 'string' },
    teamId: { type: 'string', required: true },
    status: { type: 'string', enum: ['active', 'inactive', 'archived'], default: 'active' },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.TEST_CASES]: {
    id: { type: 'string', required: true },
    testCaseId: { type: 'string' }, // Auto-generated Test Case ID (TC00001, TC00002, etc.)
    name: { type: 'string', required: true },
    description: { type: 'string' },
    project_id: { type: 'string', required: true },
    folder_id: { type: 'string' },
    type: { type: 'string', enum: ['functional', 'non-functional', 'api', 'ui', 'performance', 'security'], default: 'functional' },
    case_type: { type: 'string', enum: ['sanity', 'smoke', 'regression'], default: 'sanity' },
    priority: { type: 'string', enum: ['critical', 'high', 'medium', 'low'], default: 'medium' },
    automation_status: { type: 'string', enum: ['automated', 'manual', 'semi-automated'], default: 'manual' },
    status: { type: 'string', enum: ['active', 'draft', 'deprecated'], default: 'active' },
    steps: { type: 'array', default: [] },
    expected_result: { type: 'string' },
    prerequisites: { type: 'string' },
    testData: { type: 'string' },
    tags: { type: 'array', default: [] },
    estimated_time: { type: 'number' },
    apiConfig: { type: 'object' },
    created_by: { type: 'string', required: true },
    assigned_to: { type: 'string' },
    testRunHistory: { type: 'array', default: [] }, // Array of test run execution history
    activities: { type: 'array', default: [] }, // Array of embedded activities
    cloned_from: { type: 'string' }, // ID of the original test case (for cloned test cases)
    cloned_from_name: { type: 'string' }, // Name of the original test case (for cloned test cases)
    linked_bugs: { type: 'array', default: [] }, // Array of bug IDs related to this test case
    is_deleted: { type: 'boolean', default: false }, // Soft delete flag
    deleted_at: { type: 'datetime' }, // When the test case was deleted
    deleted_by: { type: 'string' }, // Who deleted the test case
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.TEST_RUNS]: {
    id: { type: 'string', required: true },
    testrun_id: { type: 'string' }, // Auto-generated Test Run ID (TR00001, TR00002, etc.)
    name: { type: 'string', required: true },
    description: { type: 'string' },
    project_id: { type: 'string', required: true },
    feature_id: { type: 'string' }, // DEPRECATED: Use feature_ids instead
    feature_ids: { type: 'array', default: [] }, // Array of feature IDs - supports multiple features
    status: { type: 'string', enum: ['YET_TO_START', 'IN_PROGRESS', 'PAUSED', 'COMPLETED', 'CANCELLED'], default: 'YET_TO_START' },
    environment: { type: 'string' },
    executionNotes: { type: 'string' },
    eta: { type: 'datetime' }, // Estimated completion time (date/time when test run should be completed)
    estimated_time: { type: 'number', default: 0 }, // Total estimated time in minutes (sum of all test cases' estimated_time)
    ticketLink: { type: 'string' },
    prLinks: { type: 'string' },
    urlToValidate: { type: 'string' },
    test_cases: { type: 'array', default: [] },
    comments: { type: 'array', default: [] }, // Array of comments
    activities: { type: 'array', default: [] },
    created_by: { type: 'string', required: true },
    assigned_to: { type: 'string' },
    started_at: { type: 'datetime' },
    completed_at: { type: 'datetime' },
    is_deleted: { type: 'boolean', default: false }, // Soft delete flag
    deleted_at: { type: 'datetime' }, // When the test run was deleted
    deleted_by: { type: 'string' }, // Who deleted the test run
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.BUGS]: {
    id: { type: 'string', required: true },
    bugId: { type: 'string' }, // Auto-generated Bug ID (B00001, B00002, etc.)
    title: { type: 'string', required: true },
    project_id: { type: 'string', required: true },
    test_case_ids: { type: 'array', default: [] }, // Array of linked test case IDs (mandatory - at least one required)
    feature_ids: { type: 'array', default: [] }, // Array of linked feature IDs - bidirectional relationship with features
    test_run_id: { type: 'string' },
    status: { type: 'string', enum: ['open', 'in_progress', 'resolved', 'closed', 'reopened', 'blocked', 'not_a_bug', 'setup_issue'], default: 'open' },
    priority: { type: 'string', enum: ['critical', 'high', 'medium', 'low'], default: 'medium' },
    severity: { type: 'string', enum: ['critical', 'major', 'moderate', 'minor'], default: 'moderate' },
    type: { type: 'string', enum: ['bug', 'feature', 'enhancement', 'task'], default: 'bug' },
    environment: { type: 'string', required: true },
    steps_to_reproduce: { type: 'array', required: false }, // Array of strings, one per step
    expected_result: { type: 'string', required: true },
    actual_result: { type: 'string', required: true },
    test_data: { type: 'string' }, // New field for test data
    attachments: { type: 'string' }, // Store as newline-separated URLs
    comments: { type: 'array', default: [] }, // Array of comment objects with user_id, user_name, comment, timestamp
    activities: { type: 'array', default: [] }, // Array of embedded activities
    is_deleted: { type: 'boolean', default: false }, // Soft delete flag
    deleted_at: { type: 'datetime' }, // When it was deleted
    deleted_by: { type: 'string' }, // Who deleted it
    created_by: { type: 'string', required: true },
    assigned_to: { type: 'string', required: true }, // Mandatory assignee
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.FOLDERS]: {
    id: { type: 'string', required: true },
    name: { type: 'string', required: true },
    description: { type: 'string' },
    project_id: { type: 'string', required: true },
    parent_id: { type: 'string' },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.FEATURES]: {
    id: { type: 'string', required: true },
    featureId: { type: 'string' }, // Auto-generated Feature ID (F00001, F00002, etc.)
    title: { type: 'string', required: true },
    description: { type: 'string' },
    project_id: { type: 'string', required: true },
    parent_id: { type: 'string' }, // Parent feature ID for sub-tasks
    sub_task_ids: { type: 'array', default: [] }, // Array of child feature IDs (sub-tasks)
    child_features: { type: 'array', default: [] }, // Array of child feature objects for easy access
    status: { type: 'string', enum: ['open', 'in-progress', 'in-review', 'testing', 'closed'], default: 'open' },
    priority: { type: 'string', enum: ['critical', 'high', 'medium', 'low'], default: 'medium' },
    start_date: { type: 'datetime' },
    due_date: { type: 'datetime' },
    dev_assignee: { type: 'string' }, // User ID
    qa_assignee: { type: 'string' }, // User ID
    tags: { type: 'array', default: [] },
    pr_link: { type: 'string' },
    brd_link: { type: 'string' },
    design_link: { type: 'string' },
    test_run_id: { type: 'string' }, // DEPRECATED: Use test_run_ids instead
    test_run_ids: { type: 'array', default: [] }, // Array of test run IDs - supports multiple test runs
    bug_ids: { type: 'array', default: [] }, // Array of bug IDs - bidirectional relationship with bugs
    comments: { type: 'array', default: [] }, // Array of comment objects
    activities: { type: 'array', default: [] }, // Array of activity objects
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.COMMENTS]: {
    id: { type: 'string', required: true },
    content: { type: 'string', required: true },
    entity_type: { type: 'string', required: true },
    entity_id: { type: 'string', required: true },
    project_id: { type: 'string', required: true },
    parent_id: { type: 'string' },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true },
    is_edited: { type: 'boolean', default: false },
    edited_at: { type: 'datetime' }
  },

  [COLLECTIONS.ACTIVITIES]: {
    id: { type: 'string', required: true },
    user_id: { type: 'string', required: true },
    action: { type: 'string', required: true },
    entity_type: { type: 'string' },
    entity_id: { type: 'string' },
    project_id: { type: 'string' },
    team_id: { type: 'string' },
    description: { type: 'string' },
    timestamp: { type: 'datetime', required: true }
  },

  [COLLECTIONS.REQUIREMENTS]: {
    id: { type: 'string', required: true },
    name: { type: 'string', required: true },
    description: { type: 'string' },
    project_id: { type: 'string', required: true },
    type: { type: 'string', enum: ['functional', 'non-functional', 'business'], default: 'functional' },
    status: { type: 'string', enum: ['draft', 'approved', 'implemented', 'tested'], default: 'draft' },
    priority: { type: 'string', enum: ['critical', 'high', 'medium', 'low'], default: 'medium' },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.REQUIREMENT_MAPPINGS]: {
    id: { type: 'string', required: true },
    requirement_id: { type: 'string', required: true },
    test_case_id: { type: 'string', required: true },
    project_id: { type: 'string', required: true },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  },

  [COLLECTIONS.TEST_RUN_CASES]: {
    id: { type: 'string', required: true },
    test_run_id: { type: 'string', required: true },
    test_case_id: { type: 'string', required: true },
    project_id: { type: 'string', required: true },
    status: { type: 'string', enum: ['not_started', 'in_progress', 'passed', 'failed', 'blocked', 'skipped'], default: 'not_started' },
    executed_at: { type: 'datetime' },
    executed_by: { type: 'string' },
    execution_time: { type: 'number' },
    actual_result: { type: 'string' },
    comments: { type: 'string' },
    created_by: { type: 'string', required: true },
    created_at: { type: 'datetime', required: true },
    updated_at: { type: 'datetime', required: true }
  }
}

/**
 * Required indexes for optimal performance
 */
export const REQUIRED_INDEXES = {
  [COLLECTIONS.USERS]: [
    { fields: ['email'], unique: true },
    { fields: ['role'] }
  ],
  
  [COLLECTIONS.TEAMS]: [
    { fields: ['members'] },
    { fields: ['created_by'] }
  ],
  
  [COLLECTIONS.PROJECTS]: [
    { fields: ['teamId'] },
    { fields: ['status'] },
    { fields: ['created_by'] }
  ],
  
  [COLLECTIONS.TEST_CASES]: [
    { fields: ['project_id'] },
    { fields: ['project_id', 'status'] },
    { fields: ['project_id', 'type'] },
    { fields: ['project_id', 'priority'] },
    { fields: ['folderId'] },
    { fields: ['assigned_to'] },
    { fields: ['created_by'] }
  ],
  
  [COLLECTIONS.TEST_RUNS]: [
    { fields: ['project_id'] },
    { fields: ['project_id', 'status'] },
    { fields: ['assigned_to'] },
    { fields: ['created_by'] }
  ],
  
  [COLLECTIONS.BUGS]: [
    { fields: ['project_id'] },
    { fields: ['project_id', 'status'] },
    { fields: ['project_id', 'priority'] },
    { fields: ['test_case_ids'] },
    { fields: ['feature_ids'] },
    { fields: ['test_run_id'] },
    { fields: ['assigned_to'] },
    { fields: ['created_by'] }
  ],

  [COLLECTIONS.FOLDERS]: [
    { fields: ['project_id'] },
    { fields: ['parent_id'] },
    { fields: ['created_by'] }
  ],

  [COLLECTIONS.FEATURES]: [
    { fields: ['project_id'] },
    { fields: ['parent_id'] },
    { fields: ['sub_task_ids'] },
    { fields: ['status'] },
    { fields: ['dev_assignee'] },
    { fields: ['qa_assignee'] },
    { fields: ['test_run_id'] }, // DEPRECATED: kept for backward compatibility
    { fields: ['test_run_ids'] },
    { fields: ['bug_ids'] },
    { fields: ['created_by'] }
  ],

  [COLLECTIONS.COMMENTS]: [
    { fields: ['entity_type', 'entity_id'] },
    { fields: ['project_id'] },
    { fields: ['created_by'] },
    { fields: ['created_at'] }
  ],

  [COLLECTIONS.ACTIVITIES]: [
    { fields: ['user_id'] },
    { fields: ['project_id'] },
    { fields: ['team_id'] },
    { fields: ['entity_type', 'entity_id'] },
    { fields: ['timestamp'] }
  ],

  [COLLECTIONS.REQUIREMENTS]: [
    { fields: ['project_id'] },
    { fields: ['type'] },
    { fields: ['status'] },
    { fields: ['created_by'] }
  ],

  [COLLECTIONS.REQUIREMENT_MAPPINGS]: [
    { fields: ['requirement_id'] },
    { fields: ['test_case_id'] },
    { fields: ['project_id'] }
  ],

  [COLLECTIONS.TEST_RUN_CASES]: [
    { fields: ['test_run_id'] },
    { fields: ['test_case_id'] },
    { fields: ['project_id'] },
    { fields: ['status'] },
    { fields: ['executed_by'] },
    { fields: ['created_by'] }
  ]
}

// User roles and permissions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  QA_MANAGER: 'qa_manager',
  DEVELOPER: 'developer',
  TESTER: 'tester'
}

export const PERMISSIONS = {
  // User permissions
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  USERS_DELETE: 'users:delete',
  USERS_MANAGE_ROLES: 'users:manage_roles',
  
  // Team permissions
  TEAMS_READ: 'teams:read',
  TEAMS_WRITE: 'teams:write',
  TEAMS_DELETE: 'teams:delete',
  
  // Project permissions
  PROJECTS_READ: 'projects:read',
  PROJECTS_WRITE: 'projects:write',
  PROJECTS_DELETE: 'projects:delete',
  PROJECTS_ADMIN: 'projects:admin',
  PROJECTS_CREATE_PRIVATE: 'projects:create_private',
  
  // Test case permissions
  TEST_CASES_READ: 'test_cases:read',
  TEST_CASES_WRITE: 'test_cases:write',
  TEST_CASES_DELETE: 'test_cases:delete',
  
  // Test run permissions
  TEST_RUNS_READ: 'test_runs:read',
  TEST_RUNS_WRITE: 'test_runs:write',
  TEST_RUNS_DELETE: 'test_runs:delete',
  
  // Bug permissions
  BUGS_READ: 'bugs:read',
  BUGS_WRITE: 'bugs:write',
  BUGS_DELETE: 'bugs:delete',
  BUGS_CLOSE: 'bugs:close',
  
  // Feature permissions
  FEATURES_READ: 'features:read',
  FEATURES_WRITE: 'features:write',
  FEATURES_DELETE: 'features:delete',
  
  // Report permissions
  REPORTS_READ: 'reports:read',
  REPORTS_WRITE: 'reports:write',
  REPORTS_DELETE: 'reports:delete'
}

// Role-based permission mapping
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: [
    // Full system access including user role management and private projects
    PERMISSIONS.USERS_READ, PERMISSIONS.USERS_WRITE, PERMISSIONS.USERS_DELETE, PERMISSIONS.USERS_MANAGE_ROLES,
    PERMISSIONS.TEAMS_READ, PERMISSIONS.TEAMS_WRITE, PERMISSIONS.TEAMS_DELETE,
    PERMISSIONS.PROJECTS_READ, PERMISSIONS.PROJECTS_WRITE, PERMISSIONS.PROJECTS_DELETE, PERMISSIONS.PROJECTS_ADMIN, PERMISSIONS.PROJECTS_CREATE_PRIVATE,
    PERMISSIONS.TEST_CASES_READ, PERMISSIONS.TEST_CASES_WRITE, PERMISSIONS.TEST_CASES_DELETE,
    PERMISSIONS.TEST_RUNS_READ, PERMISSIONS.TEST_RUNS_WRITE, PERMISSIONS.TEST_RUNS_DELETE,
    PERMISSIONS.BUGS_READ, PERMISSIONS.BUGS_WRITE, PERMISSIONS.BUGS_DELETE, PERMISSIONS.BUGS_CLOSE,
    PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_WRITE, PERMISSIONS.FEATURES_DELETE,
    PERMISSIONS.REPORTS_READ, PERMISSIONS.REPORTS_WRITE, PERMISSIONS.REPORTS_DELETE
  ],
  [ROLES.ADMIN]: [
    PERMISSIONS.USERS_READ, PERMISSIONS.USERS_WRITE, PERMISSIONS.USERS_DELETE,
    PERMISSIONS.TEAMS_READ, PERMISSIONS.TEAMS_WRITE, PERMISSIONS.TEAMS_DELETE,
    PERMISSIONS.PROJECTS_READ, PERMISSIONS.PROJECTS_WRITE, PERMISSIONS.PROJECTS_DELETE, PERMISSIONS.PROJECTS_ADMIN,
    PERMISSIONS.TEST_CASES_READ, PERMISSIONS.TEST_CASES_WRITE, PERMISSIONS.TEST_CASES_DELETE,
    PERMISSIONS.TEST_RUNS_READ, PERMISSIONS.TEST_RUNS_WRITE, PERMISSIONS.TEST_RUNS_DELETE,
    PERMISSIONS.BUGS_READ, PERMISSIONS.BUGS_WRITE, PERMISSIONS.BUGS_DELETE, PERMISSIONS.BUGS_CLOSE,
    PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_WRITE, PERMISSIONS.FEATURES_DELETE,
    PERMISSIONS.REPORTS_READ, PERMISSIONS.REPORTS_WRITE, PERMISSIONS.REPORTS_DELETE
  ],
  [ROLES.QA_MANAGER]: [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.TEAMS_READ,
    PERMISSIONS.PROJECTS_READ, PERMISSIONS.PROJECTS_WRITE,
    PERMISSIONS.TEST_CASES_READ, PERMISSIONS.TEST_CASES_WRITE,
    PERMISSIONS.TEST_RUNS_READ, PERMISSIONS.TEST_RUNS_WRITE,
    PERMISSIONS.BUGS_READ, PERMISSIONS.BUGS_WRITE, PERMISSIONS.BUGS_CLOSE,
    PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_WRITE, PERMISSIONS.FEATURES_DELETE,
    PERMISSIONS.REPORTS_READ, PERMISSIONS.REPORTS_WRITE
  ],
  [ROLES.DEVELOPER]: [
    PERMISSIONS.PROJECTS_READ,
    PERMISSIONS.TEST_CASES_READ, PERMISSIONS.TEST_CASES_WRITE, PERMISSIONS.TEST_CASES_DELETE,
    PERMISSIONS.TEST_RUNS_READ, PERMISSIONS.TEST_RUNS_WRITE, PERMISSIONS.TEST_RUNS_DELETE,
    PERMISSIONS.BUGS_READ, PERMISSIONS.BUGS_WRITE, PERMISSIONS.BUGS_DELETE,
    PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_WRITE, PERMISSIONS.FEATURES_DELETE
  ],
  [ROLES.TESTER]: [
    PERMISSIONS.PROJECTS_READ,
    PERMISSIONS.TEST_CASES_READ, PERMISSIONS.TEST_CASES_WRITE, PERMISSIONS.TEST_CASES_DELETE,
    PERMISSIONS.TEST_RUNS_READ, PERMISSIONS.TEST_RUNS_WRITE, PERMISSIONS.TEST_RUNS_DELETE,
    PERMISSIONS.BUGS_READ, PERMISSIONS.BUGS_WRITE, PERMISSIONS.BUGS_DELETE,
    PERMISSIONS.FEATURES_READ, PERMISSIONS.FEATURES_WRITE, PERMISSIONS.FEATURES_DELETE
  ]
}
